import {ButtonPrimary, ButtonSecondary, Checkbox, Dropdown, Input, ModalBase} from "pac-ux-components";
import React from "react";
import {
    EXPOSURE_CAP_CONFIRMATION_THRESHOLD_USD,
    formatLimit,
    LIMIT_FIELDS,
    POLICY_CURRENCY,
    resolveEffective,
    toLimitsRequest,
    validateEffective
} from "./policyLimits";

const labelStyle = {width: '100%', color: 'black', fontSize: 16, fontWeight: '400', wordWrap: 'break-word', marginBottom: 4, marginTop: 16};
const inputStyle = {background: 'white', borderRadius: 2, border: '1px #949494 solid', width: 530, height: 40, margin: '8px 0px'};
const hintStyle = {fontSize: 12, color: '#5A6B7B', marginTop: -4};

const numberOrNull = (value) => (value === null || value === undefined || value === '' ? null : Number(value));

/**
 * Airline policy form (every limit required) or tail override form (prefilled with the inherited airline
 * values and marked inherited; editing a field creates an override, clearing it returns to inherited, and
 * only the overridden limits are sent). Exposure cap changes above the threshold need an explicit
 * confirmation step that sets confirmExposureCap on the request (PYMNT-2590 design 5.4 / 11).
 *
 * `policy` is the current row being replaced (limits fully resolved, `overriddenLimits` naming the ones a
 * tail carries itself) or null when adding; `baseline` is the airline policy a tail inherits from.
 */
function PolicyFormModal({airlineIcao, baseline, policy, isBaseline, display, handleSave, closeModal, icaoOptions = []}) {
    const baselineLimits = !isBaseline && baseline ? baseline.limits || {} : null;
    const ownLimits = policy && policy.limits ? policy.limits : {};
    // A tail row comes back resolved, so only the limits it overrides itself are prefilled as its own.
    const overridden = !isBaseline && policy ? (policy.overriddenLimits || []) : LIMIT_FIELDS.map(({key}) => key);
    const initial = {};
    LIMIT_FIELDS.forEach(({key}) => {
        const own = overridden.includes(key) ? ownLimits[key] : null;
        initial[key] = own !== null && own !== undefined ? String(own) : '';
    });
    const [form, setForm] = React.useState({
        airlineIcao: airlineIcao || (policy ? policy.airlineIcao : ''),
        tailNumber: isBaseline ? '' : (policy ? policy.tailNumber : ''),
        enabled: policy ? policy.enabled !== false : true,
        ...initial,
    });
    const [saving, setSaving] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [confirming, setConfirming] = React.useState(false);

    const effective = resolveEffective(form, baselineLimits);
    // The cap in force before this write: the replaced row's resolved cap, or the airline's for a new override.
    const previousCap = policy
        ? numberOrNull(ownLimits.offlineAircraftExposureCap)
        : (baselineLimits ? numberOrNull(baselineLimits.offlineAircraftExposureCap) : null);
    const newCap = effective.offlineAircraftExposureCap;
    const capChanged = newCap !== null && newCap !== previousCap;
    const needsConfirmation = capChanged && newCap > EXPOSURE_CAP_CONFIRMATION_THRESHOLD_USD;

    const buildRequest = (confirmExposureCap) => ({
        airlineIcao: form.airlineIcao,
        tailNumber: isBaseline ? '' : form.tailNumber.trim(),
        currency: POLICY_CURRENCY,
        limits: toLimitsRequest(form),
        enabled: form.enabled,
        confirmExposureCap,
    });

    const submit = async (confirmExposureCap) => {
        setSaving(true);
        const error = await handleSave(buildRequest(confirmExposureCap));
        setSaving(false);
        if (error) {
            setMessage(error);
            setConfirming(false);
            return;
        }
        closeModal(true);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setMessage(null);
        if (!/^[A-Z]{3}$/.test(form.airlineIcao)) {
            setMessage('Airline ICAO must be exactly 3 uppercase letters');
            return;
        }
        if (!isBaseline && form.tailNumber.trim() === '') {
            setMessage('Tail number is required for an override');
            return;
        }
        if (!isBaseline && Object.keys(toLimitsRequest(form)).length === 0) {
            setMessage('A tail override must set at least one limit; leave every field inherited and retire the override instead');
            return;
        }
        const validation = validateEffective(effective, isBaseline);
        if (validation) {
            setMessage(validation);
            return;
        }
        if (needsConfirmation && !confirming) {
            setConfirming(true);
            return;
        }
        await submit(needsConfirmation);
    };

    const onLimitChange = (key, value) => {
        setConfirming(false);
        setForm({...form, [key]: value});
    };

    return (<div>
        <ModalBase style={{display: (display === true) ? 'block' : 'none'}}>
            <form onSubmit={onSubmit}>
                <div style={{width: '619px', paddingLeft: 33, fontFamily: 'Inter', maxHeight: '75vh', overflowY: 'auto'}}>
                    <div style={{width: '100%', color: 'black', fontSize: 24, fontWeight: '700', marginTop: 38}}>
                        {isBaseline ? (policy ? 'Edit airline policy' : 'Add airline policy') : (policy ? 'Edit tail override' : 'Add tail override')}
                    </div>
                    <div style={{width: '100%', color: 'black', fontSize: 16, fontWeight: '400', marginTop: 10}}>
                        {isBaseline
                            ? 'Every limit is required. These limits apply to every aircraft of the airline unless a tail override says otherwise.'
                            : 'Fields are prefilled with the inherited airline value. Editing a field creates an override; clearing it returns to inherited. Only overridden limits are sent.'}
                    </div>
                    {policy && (<div style={{...hintStyle, marginTop: 8}}>
                        Saving replaces policy {policy.id} with a new policy id; the old row is kept for the deferred charges that reference it.
                        {isBaseline ? ' The tail overrides of this airline are reissued under new ids with the same overridden values.' : ''}
                    </div>)}

                    <div style={labelStyle}>Airline ICAO</div>
                    {airlineIcao || icaoOptions.length === 0
                        ? (<Input value={form.airlineIcao}
                                  onChange={(e) => setForm({...form, airlineIcao: e.target.value.toUpperCase()})}
                                  required={true} disabled={!!airlineIcao} maxLength={3}
                                  style={inputStyle}/>)
                        : (<Dropdown options={icaoOptions}
                                     selected={form.airlineIcao}
                                     label="Select an airline"
                                     onChange={(value) => setForm({...form, airlineIcao: value})}
                                     style={{width: 530, margin: '8px 0px'}}/>)}
                    {!airlineIcao && icaoOptions.length > 0 && (<div style={hintStyle}>
                        Airlines are the ICAO codes configured on tenants.
                    </div>)}

                    {!isBaseline && (<div>
                        <div style={labelStyle}>Tail number</div>
                        <Input value={form.tailNumber} maxLength={20}
                               onChange={(e) => setForm({...form, tailNumber: e.target.value})}
                               required={true} disabled={!!policy}
                               style={inputStyle}/>
                    </div>)}

                    {LIMIT_FIELDS.map(({key, label, money}) => {
                        const inherited = baselineLimits ? formatLimit(baselineLimits[key], money) : null;
                        const isOverridden = form[key] !== '';
                        return (<div key={key}>
                            <div style={labelStyle}>
                                {label}{isBaseline ? ' *' : ''}
                                {!isBaseline && (<span style={{
                                    marginLeft: 8, fontSize: 12, padding: '2px 6px', borderRadius: 2,
                                    background: isOverridden ? '#10304C' : '#E0E0E0', color: isOverridden ? '#FFFFFF' : '#5A6B7B'
                                }}>{isOverridden ? 'Override' : 'Inherited'}</span>)}
                            </div>
                            <Input value={form[key]}
                                   type="number" step={money ? '0.01' : '1'} min="0"
                                   placeholder={inherited !== null ? `inherited: ${inherited}` : ''}
                                   onChange={(e) => onLimitChange(key, e.target.value)}
                                   required={isBaseline}
                                   style={inputStyle}/>
                            {!isBaseline && (<div style={hintStyle}>
                                {isOverridden
                                    ? `Overrides the airline value ${inherited}; clear the field to inherit again`
                                    : `Inherited from airline: ${inherited}`}
                            </div>)}
                        </div>);
                    })}

                    <div style={{display: 'flex', alignItems: 'center', marginTop: 16}}>
                        <Checkbox marked={form.enabled ? 'selected' : 'unselected'}
                                  onChange={(e) => setForm({...form, enabled: e.target.checked})}/>
                        <span style={{fontSize: 16, marginLeft: 8}}>Enabled</span>
                    </div>
                    <div style={{...hintStyle, marginTop: 4}}>
                        A disabled {isBaseline ? 'airline policy stops every aircraft of the airline from' : 'override stops this aircraft from'} taking offline payments.
                    </div>

                    {message && (<div style={{color: '#B00020', marginTop: 16, fontSize: 14}}>{message}</div>)}

                    {confirming && (<div style={{marginTop: 16, padding: 12, background: '#FFF4E5', border: '1px solid #F0B429', fontSize: 14}}>
                        Aircraft exposure cap will be set to {formatLimit(newCap, true)} {POLICY_CURRENCY}
                        {previousCap !== null ? ` (was ${formatLimit(previousCap, true)})` : ''}, above the
                        confirmation threshold of {EXPOSURE_CAP_CONFIRMATION_THRESHOLD_USD} {POLICY_CURRENCY}. The cap bounds the
                        worst case loss for the aircraft. Press "Confirm exposure cap" to save.
                    </div>)}

                    <div style={{display: 'flex', gap: 12, marginTop: 32, marginBottom: 32}}>
                        <ButtonPrimary type="submit" disabled={saving}>
                            {confirming ? 'Confirm exposure cap' : 'Save'}
                        </ButtonPrimary>
                        <ButtonSecondary type="button" onClick={() => closeModal(false)}>Cancel</ButtonSecondary>
                    </div>
                </div>
            </form>
        </ModalBase>
    </div>);
}

export default PolicyFormModal;
